import React, { ReactElement, ReactNode } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { head, isEmpty, map } from "lodash";
import {
  faCoffee,
  faUser,
  faBullhorn,
  faMoneyBill,
  faHome,
  faExternalLink,
  faTimer,
  faPhone,
  faPencilSquare,
} from "@fortawesome/pro-regular-svg-icons";
import { format } from "date-fns";
import {
  ApplicationConfiguration,
  Appointment,
  AttendeeConfiguration,
} from "../../../@types/appointments";
import { Membership } from "../../../@types";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import Translation from "components/shared/Translation";
import TranslationIndicator from "components/shared/TranslationIndicator";

function Detail({
  icon,
  type = "",
  children,
  text,
  label,
  translated,
}: {
  icon: IconProp;
  type?: string;
  children?: ReactNode[] | ReactElement;
  text?: string;
  label?: string;
  translated?: boolean;
}) {
  return (
    <div className="detail-wrap">
      <strong>
        {I18n.t(label || `js.calendars.appointment.${type}.label`)}
      </strong>
      <div className={`detail-block ${type}`}>
        <FontAwesomeIcon icon={icon} className="detail-icon" />
        {children ? children : text || ""}
        {translated && (
          <TranslationIndicator
            style={{
              display: "block",
            }}
          />
        )}
      </div>
    </div>
  );
}

type AppointmentEventDetailsArgs = {
  category: string | null;
  attendeeConfig: AttendeeConfiguration;
  consultants: string;
  applicationConfig: ApplicationConfiguration;
  organizer: string;
  contacts: Membership[];
  attendingParticipantsCount: number;
  teamsParticipantsCount: number;
  createdAt: string;
  genericAuthor?: Appointment["generic_author"];
  author: Appointment["author"];
  lastAuthor: Appointment["last_author"];
  updatedAt: string;
  translated?: boolean;
};

export default function AppointmentEventDetails({
  category,
  attendeeConfig,
  consultants,
  applicationConfig,
  organizer,
  contacts,
  attendingParticipantsCount,
  teamsParticipantsCount,
  createdAt,
  genericAuthor,
  author,
  updatedAt,
  lastAuthor,
  translated,
}: AppointmentEventDetailsArgs) {
  return (
    <div className="event-details">
      {category && <Detail type="category" icon={faCoffee} text={category} />}
      {attendeeConfig?.total && (
        <Detail type="attendee_configuration" icon={faUser}>
          {attendeeConfig.total_attendees_enforced && (
            <>{attendingParticipantsCount} /</>
          )}
          {attendeeConfig.total}
          {teamsParticipantsCount > 0 && (
            <>
              {I18n.t(
                "administration.consumer_manager.teams_participants_list_title",
                {
                  count: teamsParticipantsCount,
                },
              )}
            </>
          )}
        </Detail>
      )}
      {consultants && (
        <Detail type="consultants" icon={faBullhorn} text={consultants} />
      )}
      {applicationConfig?.registration_with_costs && (
        <Detail
          icon={faMoneyBill}
          label="js.calendars.application_configuration.costs.label"
        >
          <>
            {!applicationConfig.costs || applicationConfig.costs == 0
              ? I18n.t(
                  "js.calendars.application_configuration.costs.not_set_info",
                )
              : `${applicationConfig.costs} ${
                  applicationConfig.currency || I18n.t("js.currency")
                }`}
          </>
        </Detail>
      )}
      {organizer && (
        <Detail
          type="organizer"
          icon={faHome}
          text={organizer}
          translated={translated}
        />
      )}
      {applicationConfig?.url && (
        <Detail
          icon={faExternalLink}
          label="js.calendars.application_configuration.ticket_url.label"
        >
          {applicationConfig.url.match(/^https?:\/\//) ? (
            <a
              href={applicationConfig.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {applicationConfig.url}
            </a>
          ) : (
            <span>{applicationConfig.url}</span>
          )}
        </Detail>
      )}
      {applicationConfig?.registration_required && (
        <Detail
          icon={faTimer}
          label="js.calendars.application_configuration.registration_deadline.label"
        >
          {applicationConfig.registration_deadline ? (
            <time>
              {format(
                new Date(applicationConfig.registration_deadline),
                "dd.MM.y",
              )}
            </time>
          ) : (
            <div>
              {I18n.t(
                "js.calendars.application_configuration.registration_deadline.not_set_info",
              )}
            </div>
          )}
        </Detail>
      )}
      {!isEmpty(contacts) && (
        <Detail icon={faPhone} type="contacts">
          {contacts.length === 1 ? (
            <a href={head(contacts)?.path}>{head(contacts)?.name}</a>
          ) : (
            <ul>
              {map(contacts, (contact) => (
                <li key={contact.path}>
                  <a href={contact.path}>{contact.name}</a>
                </li>
              ))}
            </ul>
          )}
        </Detail>
      )}
      {createdAt && (
        <Detail label="js.calendars.appointment.creation" icon={faPencilSquare}>
          <Translation
            component="span"
            translation={
              genericAuthor?.path || author?.path
                ? "js.calendars.appointment.created_at_from_html"
                : "js.calendars.appointment.created_at_from_without_link"
            }
            variables={{
              at_time: format(
                new Date(createdAt),
                I18n.t("js.time.formats.date_fns.compact_datetime"),
              ),
              actor_url:
                genericAuthor?.path || author?.path
                  ? genericAuthor?.path || author?.path || ""
                  : null,
              actor_name:
                genericAuthor?.name ||
                `${author?.name}${
                  author?.detection_profile_field
                    ? ` (${author?.detection_profile_field})`
                    : ""
                }`,
            }}
          />
          <br />
          <strong>{I18n.t("js.calendars.appointment.last_edit")}</strong>
          <br />
          {format(
            new Date(updatedAt),
            I18n.t("js.time.formats.date_fns.compact_datetime") + " ",
          )}
          {lastAuthor && (
            <Translation
              component="span"
              translation={
                lastAuthor.path
                  ? "js.calendars.appointment.updated_from_html"
                  : "js.calendars.appointment.updated_from_without_link"
              }
              variables={
                lastAuthor.path
                  ? {
                      url: lastAuthor.path,
                      name: `${lastAuthor.name}${
                        lastAuthor.detection_profile_field
                          ? ` (${lastAuthor.detection_profile_field})`
                          : ""
                      }`,
                    }
                  : {
                      name: `${lastAuthor.name}${
                        lastAuthor.detection_profile_field
                          ? ` (${lastAuthor.detection_profile_field})`
                          : ""
                      }`,
                    }
              }
            />
          )}
        </Detail>
      )}
    </div>
  );
}
